import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi } from "@/api/client";
import { LIFECYCLE_KEY } from "@/api/hooks/use-lifecycle";
import { leaveFor } from "@/lib/auth";

export interface BillingProfile {
  billing_email: string | null;
  billing_legal_name: string | null;
  has_billing_address: boolean;
  has_stripe_customer: boolean;
  has_payment_method: boolean;
  /** A Self-serve Customer adds its first card in one step (ticket 20). */
  self_serve: boolean;
  /** A card can be removed only while nothing is owed. */
  card_removable: boolean;
  /** Why not, as the end of a sentence; null when removable or no card. */
  card_removal_blocked_by: string | null;
}

const BILLING_PROFILE_KEY = ["billing-profile"];

export function useBillingProfile(opts?: { pollForPaymentMethod?: boolean }) {
  return useQuery({
    queryKey: BILLING_PROFILE_KEY,
    queryFn: () => billingApi.get("profile").json<BillingProfile>(),
    // Used by the post-checkout "finalizing…" state: the payment method
    // arrives via Stripe webhook, so poll briefly until the flag flips.
    refetchInterval: opts?.pollForPaymentMethod ? 3_000 : false,
  });
}

export function useUpdateBillingProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { billing_email: string; billing_legal_name: string }) =>
      billingApi.put("profile", { json: body }).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BILLING_PROFILE_KEY });
    },
  });
}

export function useCreatePaymentMethodSetup() {
  return useMutation({
    mutationFn: () =>
      billingApi.post("payment-method/setup", { json: {} }).json<{ checkout_url: string }>(),
  });
}

/** Add or replace a card: opens Stripe's page. For a Self-serve Customer's
 * first card that one page also collects the billing details (ticket 20). */
export function useOpenCardPage() {
  return useMutation({
    mutationFn: () =>
      billingApi.post("payment-method/setup", { json: {} }).json<{ checkout_url: string }>(),
    onSuccess: ({ checkout_url }) => leaveFor(checkout_url),
  });
}

export function useRemoveCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => billingApi.delete("payment-method").json<{ removed: boolean }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BILLING_PROFILE_KEY });
      // A Self-serve Customer with no card heads for Payment grace.
      qc.invalidateQueries({ queryKey: LIFECYCLE_KEY });
    },
  });
}
